// import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';
import styled from 'styled-components';
import useSWR from 'swr';

import { Input } from 'shared/components/Input';
import { CloseX } from 'shared/icons/CloseX';
import { RecentSearchesCache } from 'search/utils/RecentSearchesCache';
import { useConstructorClient } from 'shared/hooks/useConstructorClient';
import { useDebouncedValue } from 'shared/hooks/useDebouncedValue';
import { useFocus } from 'shared/hooks/useFocus';
import { useKeyDown } from 'shared/hooks/useKeyDown';
import { setCurrentSearchTerm } from 'store/slice/searchSlice';
import { useStoreDispatch } from 'store/hooks/storeHooks';
import { variableFontRegular } from 'styles/theme/text';
import { showOnMobileAndTabletOnly } from 'styles/utils/responsiveness';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { t } from 'localization/utils/t';
import { GIFT_CARDS_SEARCH_TERMS } from 'search/enums/constants';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';

import { FeatureFlags } from 'featureFlags/constants';
import { requestService } from 'shared/utils/services/requestService';
import { useRouter } from 'next/router';
import SearchResults from './SearchResults';
import DefaultSearchView from './DefaultSearchView';

const TP = 'search.components.search.SearchBox';

function fromConsumerToConstructorSearchList(data) {
  return data?.map((item) => ({
    data: {
      ...item,
      slug: item.slug,
      image_url: item.imageUrl,
    },
    value: item.title,
  }));
}

interface ISearchBox {
  closeSearch?: () => void;
  suggestedSearches?: any[];
  error?: Error;
}

export const SearchBox = ({
  closeSearch,
  suggestedSearches,
  error: suggestionError,
}: ISearchBox) => {
  // const router = useRouter();
  const storeDispatch = useStoreDispatch();
  const focusRef = useFocus();
  const ConstructorClient = useConstructorClient();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebouncedValue(searchTerm, 500);

  const isNewSearchPageEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_SEARCH_MICROFRONTEND);

  const fetchSearchResults = useCallback(
    (searchQuery) => {
      if (!isNewSearchPageEnabled) {
        return ConstructorClient.autocomplete.getAutocompleteResults(searchQuery, {
          resultsPerSection: {
            Products: 25,
            Collections: 20,
          },
        });
      }

      const query = {
        pageOffset: '8',
        term: searchQuery,
      };

      return requestService({
        url: `/web-api/consumer-search/get-search-suggestions?${new URLSearchParams(query).toString()}`,
        method: 'GET',
      });
    },
    [ConstructorClient, isNewSearchPageEnabled],
  );

  const { data, error, isValidating } = useSWR(
    () => debouncedSearch || null,
    (searchQuery) => fetchSearchResults(searchQuery),
  );

  const productResults = !isNewSearchPageEnabled
    ? data?.sections?.Products
    : fromConsumerToConstructorSearchList(data?.data?.products || []);
  const collectionResults = !isNewSearchPageEnabled
    ? data?.sections?.Collections
    : fromConsumerToConstructorSearchList(data?.data?.collections || []);
  const resultId = data?.result_id;

  const handleChange = ({ target: { value } }) => {
    setSearchTerm(value);
    storeDispatch(setCurrentSearchTerm(value));
  };

  const handleClear = () => {
    setSearchTerm('');
    storeDispatch(setCurrentSearchTerm('TERM_UNKNOWN'));
  };

  const handleFocus = () => {
    if (!isNewSearchPageEnabled) {
      ConstructorClient?.tracker?.trackInputFocus();
    }
    sendTrackingEvent('SEARCH_BAR_TAP', {
      service_name: isNewSearchPageEnabled ? 'consumer_search' : 'constructor',
    });
  };

  const router = useRouter();

  const handleSubmit = () => {
    const newEntry = JSON.stringify({
      text: searchTerm,
      link: `/search?query=${searchTerm}`,
    });
    RecentSearchesCache.add(newEntry);
    if (!isNewSearchPageEnabled) {
      ConstructorClient?.tracker?.trackSearchSubmit(searchTerm, { original_query: searchTerm });
    }
    sendTrackingEvent('SEARCH_BAR_SEARCH_TAP', {
      search_string: searchTerm,
      search_results: productResults?.map((result) => result?.data?.slug)?.join(','),
      result_id: data?.result_id,
      service_name: isNewSearchPageEnabled ? 'consumer_search' : 'constructor',
    });

    storeDispatch(setCurrentSearchTerm(searchTerm));
    setSearchTerm('');
    if (closeSearch) {
      closeSearch();
    }
    // Send the user to gift card PDP if they search for it
    if (GIFT_CARDS_SEARCH_TERMS.includes(searchTerm.toLowerCase().trim())) {
      void router.push('/gift-cards');
    } else {
      void router.push(`/search?query=${searchTerm}&pageNumber=1`); // Use router to navigate to the search results page
    }
  };

  useKeyDown('Enter', handleSubmit);

  return (
    <>
      <SearchBox.SearchWrapper>
        <SearchBox.InputWrapper>
          <SearchBox.Input
            autoComplete="off"
            autoFocus
            data-qa="search_input"
            hasFloatingLabel={false}
            label={t(`${TP}.searchBoxInput`, 'Type to search')}
            name="search"
            onChange={handleChange}
            onFocus={handleFocus}
            ref={focusRef}
            spellCheck="false"
            value={searchTerm}
          />
          {searchTerm && (
            <SearchBox.Clear
              aria-label={t(`${TP}.clear`, 'Clear')}
              onClick={handleClear}
              data-qa="clear_search_button"
            >
              <CloseX title={t(`${TP}.clear`, 'Clear')} />
            </SearchBox.Clear>
          )}
        </SearchBox.InputWrapper>
        <SearchBox.Close type="button" data-qa="close_search_box" onClick={closeSearch}>
          {t(`${TP}.cancel`, 'Cancel')}
        </SearchBox.Close>
      </SearchBox.SearchWrapper>
      {searchTerm && (productResults?.length >= 0 || isValidating || error) ? (
        <SearchResults
          collectionResults={collectionResults}
          error={error}
          isLoading={isValidating}
          resultId={resultId}
          productResults={productResults}
          searchTerm={searchTerm}
        />
      ) : (
        <DefaultSearchView suggestedSearches={suggestedSearches} error={suggestionError} />
      )}
    </>
  );
};

SearchBox.SearchWrapper = styled.div`
  display: flex;
  padding: 12px 0 0 16px;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    padding: 0;
    border-bottom: 1px solid ${({ theme }) => theme.navigation.searchBorderColor};
  }
`;

SearchBox.InputWrapper = styled.div`
  position: relative;
  width: 100%;
  border: ${({ theme }) => theme.borders.black};

  ${Input.Content} {
    margin-top: 0;
    margin-bottom: 0;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    border: none;
    height: 36px;
  }
`;

SearchBox.Clear = styled.button`
  ${showOnMobileAndTabletOnly`
    position: absolute;
    top: 50%;
    right: 0;
    transform: translateY(-50%);
    padding: 6px 12px;
  `}
`;

SearchBox.Input = styled(Input)`
  input {
    border: none;
    margin-bottom: 0;
    padding: 8px 0 6px 10px;
    width: 90%;
    line-height: 16px;

    &:focus {
      margin-bottom: 0;
    }
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    input {
      border: none;
      padding: 13px 16px 9px;
      width: 100%;
      line-height: 14px;
    }
  }
`;

SearchBox.Close = styled.button`
  ${showOnMobileAndTabletOnly`
    ${variableFontRegular({ size: '9.5px', height: '12px' })};
    padding: 10px 16px;

    &:hover {
      text-decoration: underline;
    }
  `}
`;
